import { useState } from "react";
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Box, Button, TextField,
  MenuItem, Typography, ToggleButtonGroup, ToggleButton,
} from "@mui/material";
import { APP_PALETTE } from "../../theme/palette";
import { cashFlowApi, usdTransactionsApi } from "../../services/api";
import { formatCurrencyBs } from "../../utils/formatters";

const getTodayStr = () => {
  const now = new Date();
  const offset = now.getTimezoneOffset();
  const local = new Date(now.getTime() - offset * 60000);
  return local.toISOString().slice(0, 10);
};

const initialManualForm = { type: "expense", description: "", amount: "", date: getTodayStr() };
const initialUsdForm = { operation: "buy", usd_amount: "", exchange_rate: "", notes: "", date: getTodayStr() };

export default function CashMovementDialog({ open, onClose, onSaved }) {
  const [mode, setMode] = useState("manual");
  const [manualForm, setManualForm] = useState(initialManualForm);
  const [usdForm, setUsdForm] = useState(initialUsdForm);
  const [saving, setSaving] = useState(false);

  const resetAndClose = () => {
    setMode("manual");
    setManualForm(initialManualForm);
    setUsdForm(initialUsdForm);
    onClose();
  };

  const handleModeChange = (_, value) => {
    if (value) setMode(value);
  };

  const usdBsPreview =
    usdForm.usd_amount && usdForm.exchange_rate
      ? formatCurrencyBs(parseFloat(usdForm.usd_amount) * parseFloat(usdForm.exchange_rate))
      : "—";

  const manualValid = manualForm.description && manualForm.amount && manualForm.date;
  const usdValid = usdForm.usd_amount && usdForm.exchange_rate && usdForm.date;

  const handleSubmit = async () => {
    setSaving(true);
    try {
      if (mode === "manual") {
        await cashFlowApi.create({
          type: manualForm.type,
          description: manualForm.description,
          amount: parseFloat(manualForm.amount),
          date: manualForm.date,
        });
      } else {
        await usdTransactionsApi.create({
          operation: usdForm.operation,
          usd_amount: parseFloat(usdForm.usd_amount),
          exchange_rate: parseFloat(usdForm.exchange_rate),
          notes: usdForm.notes || undefined,
          date: usdForm.date,
        });
      }
      await onSaved();
      resetAndClose();
    } catch (error) {
      console.error("Error al guardar movimiento:", error);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={resetAndClose} maxWidth="sm" fullWidth
      PaperProps={{ sx: { borderRadius: 3 } }}>
      <DialogTitle sx={{ fontWeight: 700, color: APP_PALETTE.text.primary }}>
        Nuevo movimiento
      </DialogTitle>
      <DialogContent>
        <ToggleButtonGroup
          value={mode}
          exclusive
          onChange={handleModeChange}
          size="small"
          sx={{
            mb: 3,
            "& .MuiToggleButton-root": {
              textTransform: "none",
              px: 2,
              "&.Mui-selected": {
                backgroundColor: APP_PALETTE.brand.soft,
                color: APP_PALETTE.brand.primary,
                fontWeight: 600,
              },
            },
          }}
        >
          <ToggleButton value="manual">Ingreso / Gasto</ToggleButton>
          <ToggleButton value="usd">Dólares</ToggleButton>
        </ToggleButtonGroup>

        {mode === "manual" ? (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <TextField
              select
              size="small"
              label="Tipo"
              value={manualForm.type}
              onChange={(e) => setManualForm({ ...manualForm, type: e.target.value })}
            >
              <MenuItem value="income">Ingreso extra</MenuItem>
              <MenuItem value="expense">Gasto extra</MenuItem>
            </TextField>
            <TextField
              size="small"
              label="Descripción"
              value={manualForm.description}
              onChange={(e) => setManualForm({ ...manualForm, description: e.target.value })}
              placeholder="Ej: Pago de internet, comisión bancaria..."
            />
            <TextField
              size="small"
              type="number"
              label="Monto (Bs.)"
              inputProps={{ step: 0.01 }}
              value={manualForm.amount}
              onChange={(e) => setManualForm({ ...manualForm, amount: e.target.value })}
            />
            <TextField
              size="small"
              type="date"
              label="Fecha"
              InputLabelProps={{ shrink: true }}
              value={manualForm.date}
              onChange={(e) => setManualForm({ ...manualForm, date: e.target.value })}
            />
          </Box>
        ) : (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <TextField
              select
              size="small"
              label="Operación"
              value={usdForm.operation}
              onChange={(e) => setUsdForm({ ...usdForm, operation: e.target.value })}
            >
              <MenuItem value="buy">Compra de USD (sale Bs, entra USD)</MenuItem>
              <MenuItem value="sell">Venta de USD (sale USD, entra Bs)</MenuItem>
            </TextField>
            <Box sx={{ display: "flex", gap: 2 }}>
              <TextField
                size="small"
                type="number"
                label="Cantidad (USD)"
                inputProps={{ step: 0.01 }}
                fullWidth
                value={usdForm.usd_amount}
                onChange={(e) => setUsdForm({ ...usdForm, usd_amount: e.target.value })}
              />
              <TextField
                size="small"
                type="number"
                label="Tipo de cambio (Bs)"
                inputProps={{ step: 0.0001 }}
                fullWidth
                value={usdForm.exchange_rate}
                onChange={(e) => setUsdForm({ ...usdForm, exchange_rate: e.target.value })}
              />
            </Box>
            <Box sx={{ p: 1.5, background: APP_PALETTE.brand.soft, borderRadius: 2 }}>
              <Typography fontSize={14} fontWeight={600} color={APP_PALETTE.brand.primary}>
                Equivalente en Bs: {usdBsPreview}
              </Typography>
            </Box>
            <TextField
              size="small"
              label="Notas (opcional)"
              value={usdForm.notes}
              onChange={(e) => setUsdForm({ ...usdForm, notes: e.target.value })}
            />
            <TextField
              size="small"
              type="date"
              label="Fecha"
              InputLabelProps={{ shrink: true }}
              value={usdForm.date}
              onChange={(e) => setUsdForm({ ...usdForm, date: e.target.value })}
            />
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={resetAndClose} sx={{ color: APP_PALETTE.text.secondary }}>
          Cancelar
        </Button>
        <Button
          variant="contained"
          disabled={saving || (mode === "manual" ? !manualValid : !usdValid)}
          onClick={handleSubmit}
          sx={{
            backgroundColor: APP_PALETTE.brand.primary,
            "&:hover": { backgroundColor: APP_PALETTE.brand.primaryHover },
          }}
        >
          {saving ? "Guardando..." : "Guardar"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}